import {
  Box,
  Card,
  Grid2,
  IconButton,
  styled,
  Typography,
} from "@mui/material";
import GKButton from "components/GKButton";
import {
  DeleteIcon,
  DownloadIcon,
  ExcellconIcon,
  PdfIcon,
  UploadBigIcon,
} from "components/Icons";
import React, { useState } from "react";
import { Show } from "./Show";

interface UploadManagerFileUploadProps {
  value: File[];
  onChange: (files: File[]) => void;
  setUploadError: (files: string) => void;
  uploadError: string;
  showSupportedFormat?: boolean;
  supportName?: string;
  className?: string;
  maxHeight?: string;
  acceptType?: string;
  error?: boolean;
  disabled?: boolean;
  onDelete?: any;
  multiple?: boolean;
  sampleFile?: {
    isTrue?: boolean;
    onclick?: () => void;
    btnName?: string;
    link?: any;
  };
}

const FileInput = styled("input")(() => {
  return {
    cursor: "pointer",
    zIndex: 99,
    height: "100%",
    top: 0,
    position: "absolute",
    opacity: 0,
    width: "100%",
  };
});

const UploadManagerFileUpload = (props: UploadManagerFileUploadProps) => {
  const {
    onChange,
    value = [],
    supportName,
    acceptType,
    multiple,
    disabled,
    error,
    uploadError,
    setUploadError,
    onDelete,
    sampleFile,
  } = props;

  const [isDragOver, setIsDragOver] = useState(false);

  const acceptedTypes = acceptType
    ?.split(",")
    .map((type) => type.trim().toLowerCase());

  const isValidFileType = (file: File) => {
    if (!acceptedTypes) return true;
    const fileType = file.type.toLowerCase();
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    return (
      acceptedTypes.includes(fileType) ||
      acceptedTypes.includes(`.${fileExtension}`)
    );
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const newFiles = Array.from(event.dataTransfer.files).filter(
        isValidFileType,
      );
      if (newFiles.length > 0) {
        const allFiles = multiple ? [...value, ...newFiles] : newFiles;
        onChange(allFiles);
        setUploadError(null);
      } else {
        setUploadError("Unsupported file type.");
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.target.files && event.target.files.length > 0) {
      const newFiles = Array.from(event.target.files).filter(isValidFileType);
      if (newFiles.length > 0) {
        const allFiles = multiple ? [...value, ...newFiles] : newFiles;
        onChange(allFiles);
        setUploadError(null);
      } else {
        setUploadError("Unsupported file type.");
      }
    }
    event.target.value = "";
  };

  const handleDelete = (index: number) => {
    const newValue = value?.filter((data, i) => i !== index);
    onChange(newValue);
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        borderWidth: 1,
        borderStyle: value.length === 0 ? "dashed" : "solid",
        borderRadius: 8,
        padding: 16,
        display: "flex",
        alignItems: "center",
        justifyContent: value.length === 0 ? "center" : "",
        flexDirection: "column",
        gap: 12,
        overflow: "auto",
        transition: "all 0.3",
        background: isDragOver ? "var(--neutral-50)" : "var(--neutral-bg-100)",
        borderColor:
          error || uploadError ? "var(--warning-700)" : "var(--neutral-100)",
      }}
    >
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <Grid2
          container
          spacing={2}
          position={"relative"}
          border={value.length === 0 ? "" : `dashed 2px var(--neutral-100)`}
          borderRadius={2}
          width={"100%"}
          p={1.5}
        >
          <FileInput
            type="file"
            onChange={handleChange}
            accept={acceptType}
            disabled={disabled}
            multiple={multiple}
          />
          <Grid2
            container
            size={12}
            spacing={2}
            height={"100%"}
            display={"flex"}
            alignItems={"center"}
          >
            <Grid2
              size={value.length === 0 ? 12 : "grow"}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Box
                component={"div"}
                sx={{
                  borderRadius: 100,
                  background: isDragOver ? "var(--white)" : "var(--neutral-50)",
                  transition: "all 0.3",
                  padding: 1,
                }}
              >
                <UploadBigIcon
                  height={32}
                  width={32}
                  style={{ margin: "auto" }}
                />
              </Box>
            </Grid2>
            <Grid2
              size={value.length === 0 ? 12 : 10}
              display={"flex"}
              alignItems={value.length === 0 ? "center" : "flex-start"}
              justifyContent={"center"}
              flexDirection={"column"}
            >
              <Typography
                fontSize={14}
                fontWeight={500}
                color={"#44484F"}
                display={"flex"}
                alignItems={"center"}
                justifyContent={value.length === 0 ? "center" : "flex-start"}
              >
                Drag & Drop your file here or
                <Typography
                  style={{
                    color: "var(--primary-900)",
                    textDecoration: "underline",
                    marginLeft: 3,
                  }}
                >
                  Choose Files
                </Typography>
              </Typography>
              <Typography
                fontSize={12}
                color={"var(--neutral-500)"}
                fontWeight={500}
                display={"flex"}
                alignItems={"center"}
                justifyContent={value.length === 0 ? "center" : "flex-start"}
              >
                Supported formats :{"  "}
                <Typography color="#44484F" fontSize={12} fontWeight={500}>
                  {supportName || "All files are supported."}
                </Typography>
              </Typography>
              {uploadError && (
                <Typography
                  fontSize={12}
                  color={`var(--warning-700)`}
                  textAlign={"center"}
                >
                  {uploadError}
                </Typography>
              )}
            </Grid2>
          </Grid2>
        </Grid2>
        <Show.When isTrue={sampleFile?.isTrue && value.length === 0}>
          <Box display={"flex"} justifyContent={"center"} my={1}>
            <GKButton
              variant="outlined"
              color="dark"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                sampleFile?.onclick();
              }}
              startIcon={<DownloadIcon width={14} height={14} />}
              endIcon={
                <img
                  src={`/FileIcons/${(sampleFile?.link || "").split(".").pop()}.svg`}
                  height={30}
                  width={30}
                  alt=""
                  style={{
                    borderRadius: 4,
                    border: "1px solid",
                    borderColor: "var(--neutral-100)",
                    padding: 4,
                  }}
                />
              }
            >
              {sampleFile?.btnName || "Download Sample File"}
            </GKButton>
          </Box>
        </Show.When>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          width: "100%",
        }}
      >
        {value?.map((dataItem: any, index: any) => {
          return (
            <Grid2 container key={index}>
              <Grid2 size={12}>
                <Card
                  sx={{
                    borderColor: "var(--neutral-100)",
                    p: 1,
                    borderRadius: 2,
                  }}
                >
                  <Grid2 container alignItems={"center"}>
                    <Grid2 container alignContent={"center"} gap={2} size={11}>
                      <Grid2
                        display={"flex"}
                        alignItems={"center"}
                        bgcolor={"var(--neutral-bg-100)"}
                        p={2}
                        borderRadius={"50%"}
                      >
                        {`/FileIcons/${(dataItem?.name || "")
                          .split(".")
                          .pop()}` === "pdf" ? (
                          <PdfIcon width={32} height={32} />
                        ) : (
                          <ExcellconIcon width={32} height={32} />
                        )}
                      </Grid2>
                      <Grid2 display={"flex"} alignItems={"center"}>
                        <Typography>{dataItem?.name}</Typography>
                      </Grid2>
                    </Grid2>
                    <Grid2 size={1} textAlign={"right"}>
                      <IconButton
                        className="outline"
                        color="error"
                        onClick={() => {
                          if (onDelete) {
                            onDelete();
                          } else {
                            handleDelete(index);
                          }
                        }}
                      >
                        <DeleteIcon width={18} height={18} />
                      </IconButton>
                    </Grid2>
                  </Grid2>
                </Card>
              </Grid2>
            </Grid2>
          );
        })}
      </div>
    </div>
  );
};

export default UploadManagerFileUpload;
